const API_BASE = '/api/compliance/assets';

function getAuthHeaders(): HeadersInit {
  const token = sessionStorage.getItem('impersonation_token') || localStorage.getItem('token');
  if (!token) throw new Error('No authentication token found');
  return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(err.error || err.message || `API Error: ${res.status}`);
  }
  return res.json();
}

export interface Asset {
  id: number;
  company_id: number;
  name: string;
  asset_type: 'hardware' | 'software' | 'data' | 'service' | 'facility' | 'personnel';
  sub_type?: string;
  description?: string;
  classification: 'public' | 'internal' | 'confidential' | 'restricted';
  criticality: 'low' | 'medium' | 'high' | 'critical';
  status: 'active' | 'inactive' | 'decommissioned' | 'under_review';
  department?: string;
  location?: string;
  owner_user_id?: number;
  owner_name?: string;
  purchase_date?: string;
  end_of_life_date?: string;
  serial_number?: string;
  license_info?: string;
  next_review_date?: string;
  risk_score?: number;
  control_count?: number;
  created_at: string;
  updated_at: string;
}

export interface AssetStats {
  total: number;
  by_classification: {
    public: number;
    internal: number;
    confidential: number;
    restricted: number;
  };
  by_criticality: {
    low: number;
    medium: number;
    high: number;
    critical: number;
  };
  due_for_review: number;
  high_critical_count: number;
}

export const assetInventoryApi = {
  async getAssets(params?: {
    page?: number;
    limit?: number;
    asset_type?: string;
    classification?: string;
    criticality?: string;
    search?: string;
    company_id?: number;
  }): Promise<{ assets: Asset[]; total: number }> {
    const q = new URLSearchParams();
    if (params?.page) q.set('page', String(params.page));
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.asset_type) q.set('asset_type', params.asset_type);
    if (params?.classification) q.set('classification', params.classification);
    if (params?.criticality) q.set('criticality', params.criticality);
    if (params?.search) q.set('search', params.search);
    if (params?.company_id) q.set('company_id', String(params.company_id));
    const res = await fetch(`${API_BASE}?${q}`, { headers: getAuthHeaders() });
    return handleResponse(res);
  },

  async getAsset(id: number): Promise<Asset> {
    const res = await fetch(`${API_BASE}/${id}`, { headers: getAuthHeaders() });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async createAsset(data: Partial<Asset>): Promise<Asset> {
    const res = await fetch(API_BASE, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async updateAsset(id: number, data: Partial<Asset>): Promise<Asset> {
    const res = await fetch(`${API_BASE}/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async deleteAsset(id: number): Promise<void> {
    const res = await fetch(`${API_BASE}/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    await handleResponse(res);
  },

  async getStats(companyId?: number): Promise<AssetStats> {
    const q = companyId ? `?company_id=${companyId}` : '';
    const res = await fetch(`${API_BASE}/stats${q}`, { headers: getAuthHeaders() });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async getAssetsForReview(companyId?: number): Promise<Asset[]> {
    const q = companyId ? `?company_id=${companyId}` : '';
    const res = await fetch(`${API_BASE}/for-review${q}`, { headers: getAuthHeaders() });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async mapToControl(assetId: number, controlId: number): Promise<void> {
    const res = await fetch(`${API_BASE}/${assetId}/controls`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ control_id: controlId }),
    });
    await handleResponse(res);
  },

  async unmapFromControl(assetId: number, controlId: number): Promise<void> {
    const res = await fetch(`${API_BASE}/${assetId}/controls/${controlId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    await handleResponse(res);
  },
};
